import type { IntakeAnswers } from './types';
import type { MockProvider } from './mockProviders';
import { MOCK_PROVIDERS } from './mockProviders';

/**
 * Score a provider against intake answers. Higher = better match.
 */
export function scoreProvider(provider: MockProvider, answers: IntakeAnswers): number {
  let score = 0;

  if (answers.payType === 'insurance' && !provider.acceptsInsurance) return -1;

  const formatMatch =
    answers.format === 'no-preference' ||
    provider.format.some(
      (f) =>
        (f === 'in-person' && answers.format === 'in-person') ||
        (f === 'virtual' && answers.format === 'virtual')
    );
  if (!formatMatch) return -1;
  if (answers.format !== 'no-preference') score += 10;

  const topicOverlap = answers.topics.filter((t) =>
    provider.specialties.some((s) => s.toLowerCase().includes(t.toLowerCase()))
  ).length;
  score += topicOverlap * 5;

  const specialtyOverlap = answers.specialties.filter((s) =>
    provider.specialties.some((p) => p.toLowerCase().includes(s.toLowerCase()))
  ).length;
  score += specialtyOverlap * 3;

  if (answers.zip) {
    const sameState = sameUSState(answers.zip, provider.zip);
    if (sameState) score += 5;
  }

  return score;
}

function sameUSState(zip1: string, zip2: string): boolean {
  const s1 = stateFromZip(zip1);
  const s2 = stateFromZip(zip2);
  return s1 != null && s2 != null && s1 === s2;
}

function stateFromZip(zip: string): string | null {
  const n = parseInt(zip.slice(0, 3), 10);
  if (Number.isNaN(n)) return null;
  if (n >= 900 && n <= 966) return 'CA';
  if (n >= 840 && n <= 847) return 'UT';
  return null;
}

/**
 * Match and rank providers against intake answers.
 * Accepts an optional provider list so live mode can pass Medplum-sourced providers.
 */
export function getMatchedProviders(
  answers: IntakeAnswers,
  allProviders?: MockProvider[],
): MockProvider[] {
  const pool = allProviders ?? MOCK_PROVIDERS;
  const scored = pool
    .map((p) => ({ provider: p, score: scoreProvider(p, answers) }))
    .filter(({ score }) => score >= 0)
    .sort((a, b) => b.score - a.score);
  return scored.length > 0 ? scored.map((s) => s.provider) : [...pool];
}
